import apiClient from "./apiClient";
import { toId } from "../utils/ids";

const BASE = "/api/Quotation";

const toNum = (v) => Number(v) || 0;

// API returns quotationID (no `id`) — alias it, matching invoiceService.js/doctorService.js's
// withId convention. List rows from /all always carry lines: [] (summary only) — fetch by id
// for line detail.
const withId = (q) => ({ ...q, id: q.quotationID });

export const getQuotations = async () => {
  const res = await apiClient.get(`${BASE}/all`);
  return { ...res, data: (res.data || []).map(withId) };
};

export const getQuotation = async (id) => {
  const res = await apiClient.get(`${BASE}/${id}`);
  return { ...res, data: withId(res.data) };
};

// Two ways to identify the patient (HANDOVER_FRONTEND 4.md §8): a real patientID, or —
// for a walk-in with no Patient record — patientID: null plus all four of
// patientName/patientAge/patientGender/patientMobile, which are stored inline on the
// quotation itself. One or the other is required; neither gives a 400. Sending both is
// harmless but pointless: a valid patientID wins and the inline fields are cleared.
// Reads come back in one consistent shape either way, so nothing downstream has to branch.
// quotationDate/quotationNo/totalAmount/line amount/status are all computed server-side;
// don't send them.
const toQuotationPayload = (data) => ({
  patientID: toId(data.patientId || data.patientID),
  patientName: data.patientName || "",
  patientAge: toNum(data.patientAge) || null,
  patientGender: data.patientGender || "",
  patientMobile: data.patientMobile || "",
  quoteType: data.quoteType || "Local",
  remarks: data.remarks || "",
  lines: (data.lines || [])
    .filter((l) => l.procedureId || l.procedureID)
    .map((l) => ({
      procedureID: toId(l.procedureId || l.procedureID),
      qty: toNum(l.qty || l.quantity || 1),
      rate: toNum(l.rate),
      discount: toNum(l.discount),
    })),
});

export const createQuotation = (data) => apiClient.post(`${BASE}/save`, toQuotationPayload(data));

// Update fully replaces the line list — lines not included in the request are deleted,
// same "send complete state, not a diff" rule as Invoice.
export const updateQuotation = async (id, data) => {
  const res = await apiClient.post(`${BASE}/save`, { quotationID: toId(id), ...toQuotationPayload(data) });
  return { ...res, data: withId(res.data) };
};

export const deleteQuotation = (id) => apiClient.delete(`${BASE}/${id}`);
